import { useEffect, useLayoutEffect } from "react";
import { useNavigate } from "react-router-dom";

import { getTokenFromUrl } from "helpers/functions";
import { SjdBookingInlineData } from "helpers/global-types";
import { useStoreActions, useStoreState } from "store/store";
import { Cart } from "store/types";

export const useTokenCart = () => {
  const setCartToken = useStoreActions((a) => a.setCartToken);
  const getTokenCartThunk = useStoreActions((a) => a.getTokenCartThunk);
  const updateCartThunk = useStoreActions((a) => a.updateCartThunk);
  const setSelectedAddons = useStoreActions((a) => a.setSelectedAddons);
  const setIsGlobalLoading = useStoreActions((a) => a.setIsGlobalLoading);
  const setLoadingMessage = useStoreActions((a) => a.setLoadingMessage);
  const cartToken = useStoreState((s) => s.cartToken);
  const navigate = useNavigate();
  const url = new URL(window.location.href);

  useLayoutEffect(() => {
    const token = getTokenFromUrl();
    const pickup = url.searchParams.get("pickup");
    const destination = url.searchParams.get("destination");
    const passengers = url.searchParams.get("passengers");
    const roundtrip = url.searchParams.get("roundtrip");

    if (
      (token && token !== cartToken) ||
      pickup ||
      destination ||
      passengers ||
      roundtrip
    ) {
      // Pre-set a generic shimmer so the screen doesn't render the
      // page underneath for the few frames between this useLayoutEffect
      // and the useEffect below that picks the more specific message.
      setLoadingMessage("Setting up your reservation…");
    }
  }, []);

  useEffect(() => {
    const token = getTokenFromUrl();
    const queryToken = url.searchParams.get("token");
    if (token && token !== cartToken) {
      setLoadingMessage("Picking up where you left off…");
      const tokenFromQuery = Boolean(queryToken);
      setCartToken(token);
      getTokenCartThunk(token)
        .then((data: Cart | undefined) => {
          if (!data) return;

          // Cart itself (vehicle, passengers, destination flags, location
          // airport flags, token) was already applied inside the thunk via
          // applyCartToStore(). The hook only needs to cover the extras that
          // the thunk doesn't touch (addons in this case) and trigger the
          // step-navigation that's specific to this hook.
          if (data.addons) {
            setSelectedAddons(data.addons);
          }

          if (data.step && data.step > 1) {
            navigate(`/step-${data.step}?token=${token}`, { replace: true });
            if (tokenFromQuery) {
              window.history.replaceState(
                null,
                "",
                `${window.location.pathname}${window.location.hash}`
              );
            }
          }
        })
        .finally(() => {
          setIsGlobalLoading(false);
        });
    } else {
      const pickup = url.searchParams.get("pickup");
      const destination = url.searchParams.get("destination");
      const roundtrip = url.searchParams.get("roundtrip");
      const passengers = url.searchParams.get("passengers");
      if (pickup || destination || passengers || roundtrip) {
        setLoadingMessage("Building your trip from the link…");
        const step = pickup && destination && roundtrip ? 2 : 1;
        updateCartThunk({
          ...(pickup ? { location_id: Number(pickup) } : {}),
          ...(destination ? { destination_id: Number(destination) } : {}),
          ...(roundtrip ? { roundtrip: Number(roundtrip) } : {}),
          ...(passengers ? { passengers: Number(passengers) } : {}),
          step: step,
          ip_address: (window as SjdBookingInlineData).sjd_booking_visitor_ip,
        })
          .then(() => {
            navigate(`/step-${step}`, { replace: true });
            window.history.replaceState(
              null,
              "",
              `${url.origin}${url.pathname}#/step-${step}`
            );
            // No location.reload() here. The previous implementation
            // hard-reloaded the page when landing on step-1 because the
            // form fields didn't pick up the cart updates from the thunk
            // — step-1 was already mounted as the default `/book-now`
            // route, so its useResetForm({ defaults, cart }) hook (which
            // only re-fires on `[defaults]` change) never re-ran with
            // the populated cart. Step-1 now reacts to cart updates via
            // dedicated useEffects on cart.location / .destination /
            // .passengers / .roundtrip, which run AFTER the thunk lands
            // and populate the fields in place. The user sees the loading
            // screen for one network round-trip instead of one round-trip
            // PLUS a full page reload.
          })
          .catch(() => {})
          .finally(() => {
            setIsGlobalLoading(false);
          });
      }
    }
  }, []);
};
